import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Badge,
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Skeleton,
} from '@mlm/ui';
import { sortMembershipsByHierarchy } from '@mlm/shared';
import { listMemberships, updateMembership, type Membership } from '../../lib/api';
import { planDescription, planDisplayName } from './index';
import { SignUpAmountPerTier } from './per-tier/signup-amount';
import { DirectMatrixPerTier } from './per-tier/direct-matrix';
import { IndirectMatrixPerTier } from './per-tier/indirect-matrix';
import { MentorBonusPerTier } from './per-tier/mentor-bonus';
import { MonthlyDirectBonusPerTier } from './per-tier/monthly-direct-bonus';
import { UnilevelConsecutivePerTier } from './per-tier/unilevel-consecutive';
import { MaintenanceCashbackPerTier } from './per-tier/maintenance-cashback';
import { StairstepExclusionPerTier } from './per-tier/stairstep-exclusion';
import { BinaryDistributionCapPerTier } from './per-tier/binary-distribution-cap';
import { BinaryCycleLimitsPerTier } from './per-tier/binary-cycle-limits';
import { BinaryPairingPerTier } from './per-tier/binary-pairing';
import { JsonPerTierFallback } from './per-tier/json-fallback';
import { VortexRewardsPerTier } from './per-tier/vortex-rewards';
// VortexCycleMatrixPerTier is hidden for now — see the 'vortex' case below.

/**
 * Phase 12.5 per-tier comp-plan editor. Unlike the flat plans (which live
 * in `comp_plan_configs`), these plans store their config inside each
 * `memberships.config` row. This component loads ALL memberships, lets the
 * admin edit per-tier values for the selected plan, and saves by issuing
 * one `PATCH /memberships/:id` per tier whose config changed.
 *
 * Per-tier configs are NOT versioned (unlike comp_plan_configs). The
 * Phase 11 audit log captures `membership.update` rows for the trail.
 */
export function PerTierPlanEditor({
  planCode,
  canWrite,
  descriptionOverride,
  binaryVariant,
}: {
  planCode: string;
  canWrite: boolean;
  /** Override the plan description (e.g. when this editor is a per-tier add-on to a flat plan like Vortex). */
  descriptionOverride?: string;
  /** Binary only: the live variant ('legacy' | 'teslab'), selecting which
   *  per-tier binary table to show. Legacy → daily cycle limits; Teslab →
   *  per-run distribution cap. */
  binaryVariant?: string;
}) {
  const qc = useQueryClient();
  const membershipsQ = useQuery({
    queryKey: ['admin-memberships'],
    queryFn: listMemberships,
  });
  // Ordered ascending by tier hierarchy so the matrix rows/columns match the
  // Memberships page (and don't read as an arbitrary alphabetical jumble).
  const memberships = useMemo(
    () => sortMembershipsByHierarchy(membershipsQ.data ?? []),
    [membershipsQ.data],
  );

  // Draft keyed by membership id. Initialized from the loaded configs and
  // re-initialized when the plan changes OR the memberships query refreshes.
  const [draft, setDraft] = useState<Record<string, Record<string, unknown>>>({});
  useEffect(() => {
    const next: Record<string, Record<string, unknown>> = {};
    for (const m of memberships) {
      next[m.id] = (m.config ?? {}) as Record<string, unknown>;
    }
    setDraft(next);
  }, [memberships, planCode]);

  function setMembershipConfig(id: string, nextCfg: Record<string, unknown>) {
    setDraft((prev) => ({ ...prev, [id]: nextCfg }));
  }

  const dirtyIds = useMemo(() => {
    const ids: string[] = [];
    for (const m of memberships) {
      const original = JSON.stringify(m.config ?? {});
      const current = JSON.stringify(draft[m.id] ?? {});
      if (original !== current) ids.push(m.id);
    }
    return ids;
  }, [memberships, draft]);

  const saveMut = useMutation({
    mutationFn: async () => {
      for (const id of dirtyIds) {
        await updateMembership(id, { config: draft[id] });
      }
    },
    meta: { successMessage: 'Per-tier rates saved.' },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin-memberships'] });
    },
  });

  function resetDraft() {
    const next: Record<string, Record<string, unknown>> = {};
    for (const m of memberships) {
      next[m.id] = (m.config ?? {}) as Record<string, unknown>;
    }
    setDraft(next);
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          {planDisplayName(planCode)}
          <Badge variant="muted">per-tier</Badge>
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-5">
        <p className="text-sm text-muted-foreground">
          {descriptionOverride ?? planDescription(planCode)}
        </p>

        {membershipsQ.isLoading ? (
          <Skeleton className="h-40 w-full" />
        ) : memberships.length === 0 ? (
          <p className="text-sm text-muted-foreground border rounded-md p-4">
            No membership tiers configured yet. Create them under Members →
            Memberships first.
          </p>
        ) : (
          <PerTierPlanBody
            planCode={planCode}
            memberships={memberships}
            draft={draft}
            onChange={setMembershipConfig}
            readOnly={!canWrite}
            binaryVariant={binaryVariant}
          />
        )}

        {canWrite && memberships.length > 0 ? (
          <div className="flex items-center gap-2 pt-3 border-t">
            <Button
              onClick={() => saveMut.mutate()}
              disabled={dirtyIds.length === 0 || saveMut.isPending}
            >
              {saveMut.isPending
                ? 'Saving…'
                : dirtyIds.length === 0
                  ? 'No changes'
                  : `Save ${dirtyIds.length} tier${dirtyIds.length > 1 ? 's' : ''}`}
            </Button>
            <Button
              variant="outline"
              disabled={dirtyIds.length === 0 || saveMut.isPending}
              onClick={resetDraft}
            >
              Reset
            </Button>
            {saveMut.isError ? (
              <span className="text-xs text-destructive">
                {String((saveMut.error as Error)?.message ?? 'save failed')}
              </span>
            ) : null}
            {saveMut.isSuccess && dirtyIds.length === 0 ? (
              <span className="text-xs text-success">Saved.</span>
            ) : null}
          </div>
        ) : null}
      </CardContent>
    </Card>
  );
}

function PerTierPlanBody({
  planCode,
  memberships,
  draft,
  onChange,
  readOnly,
  binaryVariant,
}: {
  planCode: string;
  memberships: Membership[];
  draft: Record<string, Record<string, unknown>>;
  onChange: (id: string, next: Record<string, unknown>) => void;
  readOnly: boolean;
  binaryVariant?: string;
}) {
  switch (planCode) {
    case 'sign_up_bonus':
      return (
        <SignUpAmountPerTier
          memberships={memberships}
          draft={draft}
          onChange={onChange}
          readOnly={readOnly}
          configKey="sign_up_bonus"
          amountLabel="Cash"
          secondaryConfigKey="sign_up_bonus_gc"
          secondaryLabel="GC"
          hint="One-time credit to the new slot's owner on activation, drawn from the new slot's OWN tier. Set a Cash and/or GC amount per tier (either, both, or neither)."
        />
      );
    case 'sign_up_vortex':
      return (
        <SignUpAmountPerTier
          memberships={memberships}
          draft={draft}
          onChange={onChange}
          readOnly={readOnly}
          configKey="sign_up_vortex"
          hint="Sibling to sign-up bonus. Pays in parallel to the same slot owner. Use either, both, or neither."
        />
      );
    case 'direct':
      return (
        <DirectMatrixPerTier
          memberships={memberships}
          draft={draft}
          onChange={onChange}
          readOnly={readOnly}
        />
      );
    case 'direct_kit_price':
      return (
        <DirectMatrixPerTier
          memberships={memberships}
          draft={draft}
          onChange={onChange}
          readOnly={readOnly}
          configKey="direct_kit_price"
          caption={
            <>
              Rows = <strong>receiver tier</strong> (the direct sponsor earning the
              bonus). Columns = <strong>buyer tier</strong> (the new slot's tier).
              Cell = <strong>percent</strong> of the discounted kit price the new
              member paid, credited to the sponsor at slot activation. Blank /
              zero = no payout for that pair.
            </>
          }
        />
      );
    case 'australian_pass_up':
      return (
        <DirectMatrixPerTier
          memberships={memberships}
          draft={draft}
          onChange={onChange}
          readOnly={readOnly}
          configKey="australian_pass_up"
          caption={
            <>
              Rows = <strong>earner tier</strong> (the slot the income lands on —
              the sponsor, or the ancestor it passes up to). Columns ={' '}
              <strong>buyer tier</strong> (the newly activated slot's tier).
              Cell = flat cash paid to the earner. Blank / zero = the pass-up
              income is forfeited for that pair.
            </>
          }
        />
      );
    case 'indirect':
      return (
        <IndirectMatrixPerTier
          memberships={memberships}
          draft={draft}
          onChange={onChange}
          readOnly={readOnly}
        />
      );
    case 'infinity_bonus':
      return (
        <SignUpAmountPerTier
          memberships={memberships}
          draft={draft}
          onChange={onChange}
          readOnly={readOnly}
          configKey="infinity_bonus"
          hint="Infinity Bonus BASE for this (entrant) tier. On activation each upline earns base × (rank override % − running max). Set rank override %s under the Stairstep editor."
        />
      );
    case 'mentor_bonus':
      return (
        <MentorBonusPerTier memberships={memberships} draft={draft} onChange={onChange} readOnly={readOnly} />
      );
    case 'monthly_direct_bonus':
      return (
        <MonthlyDirectBonusPerTier memberships={memberships} draft={draft} onChange={onChange} readOnly={readOnly} />
      );
    case 'unilevel_consecutive_bonus':
      return (
        <UnilevelConsecutivePerTier memberships={memberships} draft={draft} onChange={onChange} readOnly={readOnly} />
      );
    case 'maintenance_cashback':
      return (
        <MaintenanceCashbackPerTier memberships={memberships} draft={draft} onChange={onChange} readOnly={readOnly} />
      );
    case 'stairstep':
      return (
        <StairstepExclusionPerTier memberships={memberships} draft={draft} onChange={onChange} readOnly={readOnly} />
      );
    case 'binary':
      // Legacy/Extreme → per-tier daily cycle limits; Teslab → per-run
      // distribution cap. Extreme ALSO gets per-tier pairing tables + GC riders.
      // (Unknown/undefined variant falls through to the cycle limits.)
      return (
        <div className="space-y-6">
          {binaryVariant !== 'teslab' && (
            <BinaryCycleLimitsPerTier memberships={memberships} draft={draft} onChange={onChange} readOnly={readOnly} />
          )}
          {binaryVariant === 'extreme' && (
            <BinaryPairingPerTier memberships={memberships} draft={draft} onChange={onChange} readOnly={readOnly} />
          )}
          {binaryVariant === 'teslab' && (
            <BinaryDistributionCapPerTier memberships={memberships} draft={draft} onChange={onChange} readOnly={readOnly} />
          )}
        </div>
      );
    case 'vortex':
      return (
        <div className="space-y-6">
          <VortexRewardsPerTier memberships={memberships} draft={draft} onChange={onChange} readOnly={readOnly} />
          <div className="space-y-1">
            <p className="text-xs font-medium">Direct-recruit tokens (membership matrix)</p>
            <DirectMatrixPerTier
              memberships={memberships}
              draft={draft}
              onChange={onChange}
              readOnly={readOnly}
              configKey="vortex_direct_token"
              caption={
                <>
                  Rows = <strong>sponsor tier</strong> (the recruiter earning the
                  tokens). Columns = <strong>recruit tier</strong> (the newly
                  activated slot's tier). Cell = tokens granted to the sponsor on
                  a paid recruit's activation (they can immediately mint a Vortex
                  entry). Blank / zero = no direct-recruit tokens for that pair.
                </>
              }
            />
          </div>
          {/* Per-tier Cycle income matrix is hidden for now (paired with the
              flat downline/upline depth hidden in VortexEditor). Existing values
              are preserved; restore this block to re-expose the editor. */}
        </div>
      );
    default:
      // Genuinely unknown per-tier plan — labeled raw-JSON last resort only.
      return (
        <JsonPerTierFallback
          planCode={planCode}
          memberships={memberships}
          draft={draft}
          onChange={onChange}
          readOnly={readOnly}
        />
      );
  }
}
